import React, {Fragment, useEffect, useState} from 'react';
import gageIcon from "assets/gage.svg";
import Button from "../Button";
import Icon from "../Icon";

// Helpers ----

function renderContentFields(contentFields: string[]) {
  return contentFields.map((field, index) => {
    const isLast = index === contentFields.length - 1
    const isPenultimate = index === contentFields.length - 2

    return (
      <Fragment key={`${field}-${index}`}>
        <b className='font-medium'>{field}</b>
        {!isLast && (isPenultimate ? ' and ' : ', ')}
      </Fragment>
    )
  })
}

// Types ----

export type AIContentStatus = 'success' | 'error'

type GageGenerateContentProps = {
  onClick: () => void
  loading?: boolean
  disabled?: boolean
  status: AIContentStatus | null
  contentFields?: string[]
}

// Component ----

function GageGenerateContent({onClick, loading = false, disabled = false, status, contentFields = []}: GageGenerateContentProps) {
  const [showStatus, setShowStatus] = useState<boolean>(false);

  const showContentFields = !!contentFields.length;

  // Display status message for 5 seconds
  useEffect(() => {
    if (!status) return;

    setShowStatus(true);
    const timeout = setTimeout(() => setShowStatus(false), 5000);

    return () => clearTimeout(timeout);
  }, [status])

  return (
    <div className='flex flex-col items-start gap-4 border border-gray-200 p-3 rounded-lg max-w-2xl'>
      <div className='flex gap-4 w-full'>
        <div className='flex flex-col gap-2'>
          <h4 className='text-gray-800 text-md font-medium'>Need a helping hand from Gage?</h4>

          {showContentFields &&
            <p className='text-gray-500 text-sm font-normal'>
              Generate content for {renderContentFields(contentFields)}.
            </p>
          }
        </div>
        <img src={gageIcon} alt="Gage Icon" className={`ml-auto w-16 h-16 transition-opacity duration-500 ${disabled? 'grayscale opacity-50' : ''}`} width={60} height={60} />
      </div>

      <div className='flex flex-wrap gap-4 items-center'>
        <Button
          color={Button.colors.SECONDARY}
          size={Button.sizes.SMALL}
          disabled={disabled}
          onClick={onClick}
          showSpinner={loading}
        >
          {loading ? 'Generating content' : 'Generate content'}
        </Button>

        {(status === 'success' && showStatus) &&
          <div className='flex gap-2 items-center'>
            <div className='bg-green-600 rounded-full p-0.5'>
              <Icon name='check' className='text-white size-3.5' />
            </div>
            <p className='text-green-700 text-sm'>
              Successfully generated content!
            </p>
          </div>
        }
        {(status === 'error' && showStatus) &&
          <div className='flex gap-2 items-center'>
            <div className='bg-red-600 rounded-full p-0.5'>
              <Icon name='x' className='text-white size-3.5' />
            </div>
            <p className='text-red-600 text-sm'>
              Sorry something went wrong, please try again.
            </p>
          </div>
        }
      </div>

      <p className='text-gray-500 text-xs'>Gage can make mistakes. Please check all generated content.</p>
    </div>
  );
}

export {GageGenerateContent};
